import React, { useEffect, useState, useContext, useRef, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { CheckIcon, DownloadSimpleIcon, CaretDoubleDownIcon, CaretDoubleUpIcon, XIcon, ListPlusIcon, FileCloudIcon, FunnelIcon, PlusIcon, PaperclipIcon, BookmarksSimpleIcon } from "@phosphor-icons/react";
import { getMediaType, recordStat, isRestrictedFileType, extractPathAfterBulk, parseCitationPageFromSourceLike, isMetaFile } from '@/shared/functions';
import { Tooltip } from '@heroui/tooltip';
import { MediaDisplay, MediaDisplayRef, fetchMboxData } from './ui/mediaDisplay';
import { MetaSocialViewerButton } from './ui/metaPreviewViewer';
import { MboxAttachment, MediaObject, MediaType, SourceInfo } from '@/shared/types';
import { Source } from '../shared/types';
import { Card, CardHeader, CardBody } from "@heroui/card";
import { FeaturesContext, StatsContext } from '@/app/contexts';
import { EventFromChat } from './answer-item';
import { Spinner } from '@heroui/spinner';
import { getCurrentSourceInfo } from '@/shared/clientFunctions';
import { UsableMediaTypes } from '@/shared/constants';

/** Citation page only when explicitly present on the source (avoids inventing page 1). */
function citationPageIfSpecified(source: Source): number | undefined {
  const raw = source.page ?? source.pageNumber ?? source.sourceContext?.pageNumber;
  if (raw === undefined || raw === null || raw === '') return undefined;
  const n = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
  if (Number.isFinite(n) && n >= 1) return n;
  return undefined;
}

/**
 * Clone for addToTimeline: preserve audio/video start & end in sourceContext; for other types,
 * merge an explicit citation page without wiping existing context (unlike assigning only pageNumber).
 */
function buildTimelinePrimarySource(source: Source): Source {
  const mediaType = getMediaType(source, source.sourceInfo);
  const isAudioVideo = mediaType === 'audio' || mediaType === 'video';
  const baseContext = {
    ...(source.sourceContext || {})
  };
  const sourceContext = isAudioVideo ? baseContext : (() => {
    const pageNum = citationPageIfSpecified(source);
    return pageNum !== undefined ? {
      ...baseContext,
      pageNumber: pageNum
    } : baseContext;
  })();
  const topPage = !isAudioVideo ? citationPageIfSpecified(source) : undefined;
  return {
    ...source,
    sourceContext,
    ...(topPage !== undefined ? {
      pageNumber: topPage
    } : {})
  };
}
function mediaContextForTimelineMediaRow(timelinePrimary: Source, rowMediaType: MediaType): Record<string, unknown> | undefined {
  if (rowMediaType === 'audio' || rowMediaType === 'video') {
    const ctx = timelinePrimary.sourceContext;
    if (!ctx) return undefined;
    if (ctx.startTime == null && ctx.endTime == null) return undefined;
    return {
      ...ctx
    };
  }
  const pageNum = citationPageIfSpecified(timelinePrimary);
  if (pageNum !== undefined) {
    return {
      ...(timelinePrimary.sourceContext || {}),
      pageNumber: pageNum
    };
  }
  const ctx = timelinePrimary.sourceContext;
  if (ctx?.pageNumber != null) return {
    ...ctx
  };
  return undefined;
}
interface SourceCardProps {
  sources: Source[];
  selectedSourceIndex: number;
  answerIndex: number;
  epochTimeSecs: number;
  handleDownload: (source: Source) => void;
  handleAddSourceToScope: (source: Source, index: number) => void;
  handleCopySourceForTimeline: (source: Source, index: number) => void;
  handleCopyToMediaShare?: (source: Source, index: number, onSuccess?: () => void) => void;
  setIsExpanded: React.Dispatch<React.SetStateAction<boolean>> | undefined;
  fullScreenSource: Source | undefined;
  copiedSource: number;
  addedSource: number;
  closeSources: () => void;
  addToTimeline: (event: EventFromChat) => void;
  caseId?: string | null;
}
export const SourceCard: React.FC<SourceCardProps> = ({
  sources,
  selectedSourceIndex,
  answerIndex,
  epochTimeSecs,
  handleDownload,
  handleAddSourceToScope,
  handleCopySourceForTimeline,
  handleCopyToMediaShare,
  setIsExpanded,
  fullScreenSource,
  copiedSource,
  addedSource,
  closeSources,
  addToTimeline,
  caseId
}) => {
  const [createdEventFromSource, setCreatedEventFromSource] = useState(false);
  const [sourceInfo, setSourceInfo] = useState<SourceInfo | null>(null);
  const [loadingSourceInfo, setLoadingSourceInfo] = useState(true);
  const [isCitationExpanded, setIsCitationExpanded] = useState(() => {
    const mediaType = getMediaType(sources[selectedSourceIndex], null);
    return !['image', 'video', 'audio'].includes(mediaType);
  });
  const [attachmentUrl, setAttachmentUrl] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<MboxAttachment[] | null>(null);
  const [openAttachmentIndex, setOpenAttachmentIndex] = useState<number | null>(null);
  const [copiedAttachementIndex, setCopiedAttachementIndex] = useState<number | null>(null);
  const [createdEventFromAttachment, setCreatedEventFromAttachment] = useState<number | null>(null);
  const [copiedToMediaShareIndex, setCopiedToMediaShareIndex] = useState<number | null>(null);
  const [copiedToMediaShareAttachmentIndex, setCopiedToMediaShareAttachmentIndex] = useState<number | null>(null);
  const sourceCardRef = useRef<HTMLDivElement>(null);
  const mediaDisplayRef = useRef<MediaDisplayRef>(null);
  const currentMboxFetchRef = useRef<{
    sourceFilePath: string | undefined;
    abortController: AbortController;
  } | null>(null);
  const statsContext = useContext(StatsContext);
  const {
    featureFlags
  } = useContext(FeaturesContext) || {};
  const handleCopyAttachmentSourceForTimeline = (attachment: MboxAttachment, attachmentIndex: number) => {
    // convert MboxAttachment to Source
    const source = {
      sourceFilePath: attachment.attachmentUrl,
      sourceContext: {
        attachmentUrl: attachment.attachmentUrl
      }
    } as Source;
    // Use a negative value to ensure it doesn't match selectedSourceIndex (which is >= 0)
    // This prevents the main source copy button from showing "copied" when only an attachment is copied
    handleCopySourceForTimeline(source, -1 - attachmentIndex);
    setCopiedAttachementIndex(attachmentIndex);
    setTimeout(() => {
      setCopiedAttachementIndex(null);
    }, 1500);
  };
  const handleCreateEventFromAttachment = (source: Source, attachment: MboxAttachment, attachmentIndex: number) => {
    setCreatedEventFromAttachment(attachmentIndex);
    setTimeout(() => {
      setCreatedEventFromAttachment(null);
    }, 2000);
    // convert MboxAttachment to Source
    const sourceFromAttachment = {
      sourceFilePath: attachment.attachmentUrl,
      sourceContext: {
        attachmentUrl: attachment.attachmentUrl
      },
      content: source.content
    } as Source;
    handleCreateEventFromSource(sourceFromAttachment, epochTimeSecs);
  };
  const handleCreateEventFromSource = (source: Source, epochTimeSecs: number) => {
    setCreatedEventFromSource(true);
    setTimeout(() => {
      setCreatedEventFromSource(false);
    }, 2000);
    let sourceForTimeline = buildTimelinePrimarySource(source);
    let sourcesList: Source[] = [sourceForTimeline];
    let mediaSources: MediaObject[] = [];
    const isMboxFile = sourceForTimeline.sourceFilePath?.endsWith('.mbox') || sourceForTimeline.sourceFilePath?.endsWith('.mbx');
    if (isMboxFile) {
      const mboxRowType = getMediaType({
        mediaType: 'unknown',
        mediaUrl: sourceForTimeline.sourceFilePath
      } as MediaObject, sourceForTimeline.sourceInfo);
      const mboxMc = mediaContextForTimelineMediaRow(sourceForTimeline, mboxRowType);
      mediaSources.push({
        mediaUrl: sourceForTimeline.sourceFilePath,
        mediaType: mboxRowType,
        ...(mboxMc ? {
          mediaContext: mboxMc
        } : {})
      } as MediaObject);
    }
    if (sourceForTimeline.sourceContext?.attachmentUrl || !!attachmentUrl) {
      const attachmentUrlToUse = sourceForTimeline.sourceContext?.attachmentUrl || attachmentUrl;
      sourceForTimeline = {
        ...sourceForTimeline,
        sourceContext: {
          ...sourceForTimeline.sourceContext,
          attachmentUrl: attachmentUrlToUse
        }
      };
      sourcesList = [sourceForTimeline];
      const attachmentMediaType = getMediaType({
        mediaType: 'unknown',
        mediaUrl: attachmentUrlToUse
      } as MediaObject, {
        mediaType: 'unknown',
        mediaUrl: attachmentUrlToUse
      } as SourceInfo);
      const attMc = mediaContextForTimelineMediaRow(sourceForTimeline, attachmentMediaType);
      sourcesList.push({
        sourceFilePath: attachmentUrlToUse,
        sourceUrl: attachmentUrlToUse
      } as Source);
      mediaSources.push({
        mediaUrl: attachmentUrlToUse,
        mediaType: attachmentMediaType,
        ...(attMc ? {
          mediaContext: attMc
        } : {})
      } as MediaObject);
    }
    if (isMboxFile && !!attachments && attachments.length > 0) {
      attachments.forEach(attachment => {
        if (attachment.attachmentUrl && attachment.attachmentUrl !== attachmentUrl) {
          sourcesList.push({
            sourceFilePath: attachment.attachmentUrl,
            sourceUrl: attachment.attachmentUrl
          } as Source);
          const mediaType = getMediaType({
            mediaType: 'unknown',
            mediaUrl: attachment.attachmentUrl
          } as MediaObject, source.sourceInfo);
          if (UsableMediaTypes.includes(mediaType)) {
            mediaSources.push({
              mediaUrl: attachment.attachmentUrl,
              mediaType: mediaType
            } as MediaObject);
          }
        }
      });
    }
    addToTimeline({
      answerText: 'FROM_SOURCE',
      sources: sourcesList,
      media: mediaSources,
      autogen: false,
      epochTimeSecs: epochTimeSecs,
      slideTitle: '',
      slideDescription: sourceForTimeline.content || ''
    } as EventFromChat);
    closeSources();
  };
  useEffect(() => {
    setLoadingSourceInfo(true);
    let newSourceInfo = null;
    // don't get source info if preview file path is available from chat or is an mbox file
    const aSource = sources[selectedSourceIndex];
    const sourceFilePath = aSource?.sourceFilePath;
    const isMboxFile = sourceFilePath?.endsWith('.mbox') || sourceFilePath?.endsWith('.mbx');
    if (isMboxFile) {
      // Cancel any previous mbox fetch to prevent stale data from being set
      if (currentMboxFetchRef.current) {
        currentMboxFetchRef.current.abortController.abort();
      }

      // Clear mbox-specific states immediately when switching between mbox files
      // to prevent displaying stale data from the previously viewed mbox file
      setAttachmentUrl(null);
      setAttachments(null);
      setOpenAttachmentIndex(null);

      // Create a new AbortController for this fetch
      const abortController = new AbortController();
      currentMboxFetchRef.current = {
        sourceFilePath,
        abortController
      };
      const fetchMbox = async () => {
        // Capture the sourceFilePath at the start of the fetch to verify it's still current
        const fetchSourceFilePath = sourceFilePath;
        let citedAttachmentUrl = null;
        const analysisFilePath = aSource?.analysisFilePath;
        if (!!analysisFilePath && analysisFilePath !== sourceFilePath) {
          citedAttachmentUrl = analysisFilePath;
          // Only set state if this fetch is still for the current source
          if (currentMboxFetchRef.current?.sourceFilePath === fetchSourceFilePath) {
            setAttachmentUrl(citedAttachmentUrl);
          }
        } else {
          // Clear attachmentUrl if switching to mbox without cited attachment
          // Only set state if this fetch is still for the current source
          if (currentMboxFetchRef.current?.sourceFilePath === fetchSourceFilePath) {
            setAttachmentUrl(null);
          }
        }
        const mboxData = await fetchMboxData(sourceFilePath, aSource?.previewFilePath || (aSource as MediaObject)?.mediaContext?.previewUrl || undefined, abortController.signal);

        // Only update state if this fetch is still for the current source
        // This prevents stale data from a previous fetch overwriting current data
        if (currentMboxFetchRef.current?.sourceFilePath !== fetchSourceFilePath) {
          return;
        }
        if (!!mboxData && !!mboxData.attachments && mboxData.attachments.length > 0) {
          setAttachments(mboxData.attachments || []);
          if (!!citedAttachmentUrl) {
            const attachementToOpenIndex = mboxData.attachments.find((anAttachment: MboxAttachment) => anAttachment.attachmentUrl === citedAttachmentUrl)?.attachmentIndex;
            if (attachementToOpenIndex !== undefined && attachementToOpenIndex !== null) {
              setOpenAttachmentIndex(attachementToOpenIndex);
            } else {
              // Clear openAttachmentIndex if cited attachment not found
              setOpenAttachmentIndex(null);
            }
          } else {
            // Clear openAttachmentIndex if no cited attachment
            setOpenAttachmentIndex(null);
          }
        } else {
          // Clear attachments and openAttachmentIndex if no attachments found
          setAttachments(null);
          setOpenAttachmentIndex(null);
        }
      };
      fetchMbox();
      const mediaTypeToTest = getMediaType(sources[selectedSourceIndex], newSourceInfo);
      const isMboxFileForCitation = sources[selectedSourceIndex]?.sourceFilePath?.endsWith('.mbox') || sources[selectedSourceIndex]?.sourceFilePath?.endsWith('.mbx');
      if (['image', 'audio', 'video'].includes(mediaTypeToTest) || isMboxFileForCitation) {
        setIsCitationExpanded(false);
      }
      setSourceInfo(newSourceInfo);
      setLoadingSourceInfo(false);
    } else if (!sources[selectedSourceIndex]?.previewFilePath && !!sources[selectedSourceIndex]?.sourceFilePath) {
      // Cancel any pending mbox fetch when switching to non-mbox source
      if (currentMboxFetchRef.current) {
        currentMboxFetchRef.current.abortController.abort();
        currentMboxFetchRef.current = null;
      }
      // Clear mbox-specific states when switching to non-mbox source
      setAttachmentUrl(null);
      setAttachments(null);
      setOpenAttachmentIndex(null);
      try {
        getCurrentSourceInfo(sources[selectedSourceIndex]?.sourceFilePath).then(sourceInfoObject => {
          newSourceInfo = sourceInfoObject;
        });
      } catch (error) {
        // ignore
      }
      const mediaTypeToTest = getMediaType(sources[selectedSourceIndex], newSourceInfo);
      const isMboxFileForCitation = sources[selectedSourceIndex]?.sourceFilePath?.endsWith('.mbox') || sources[selectedSourceIndex]?.sourceFilePath?.endsWith('.mbx');
      if (['image', 'audio', 'video'].includes(mediaTypeToTest) || isMboxFileForCitation) {
        setIsCitationExpanded(false);
      }
      setSourceInfo(newSourceInfo);
      setLoadingSourceInfo(false);
    } else {
      // Cancel any pending mbox fetch when switching to non-mbox source
      if (currentMboxFetchRef.current) {
        currentMboxFetchRef.current.abortController.abort();
        currentMboxFetchRef.current = null;
      }
      // Source has previewFilePath (or no sourceFilePath), so no need to fetch source info
      // Clear mbox-specific states when switching to non-mbox source
      setAttachmentUrl(null);
      setAttachments(null);
      setOpenAttachmentIndex(null);
      setSourceInfo(null);
      setLoadingSourceInfo(false);
    }

    // Cleanup function to cancel any pending fetch when component unmounts or effect re-runs
    return () => {
      if (currentMboxFetchRef.current) {
        currentMboxFetchRef.current.abortController.abort();
        currentMboxFetchRef.current = null;
      }
    };
  }, [sources[selectedSourceIndex]?.sourceFilePath, sources[selectedSourceIndex]?.analysisFilePath, sources[selectedSourceIndex]?.previewFilePath, selectedSourceIndex]);
  const sourceCardMediaItem = useMemo(() => ({
    ...sources[selectedSourceIndex],
    sourceInfo: sourceInfo,
    originalUrl: sources[selectedSourceIndex].sourceFilePath,
    transcriptUrl: sources[selectedSourceIndex].location?.includes('.transcript.') ? sources[selectedSourceIndex].location : sources[selectedSourceIndex].sourceInfo?.transcriptObjectUrl || sources[selectedSourceIndex].sourceContext?.transcriptUrl,
    mediaUrl: (() => {
      const source = fullScreenSource ? fullScreenSource : sources[selectedSourceIndex];
      let finalUrl = source.sourceFilePath;
      if (source.previewFilePath && source.previewFilePath !== source.sourceFilePath) {
        finalUrl = source.previewFilePath;
      }
      return finalUrl;
    })(),
    mediaType: getMediaType(fullScreenSource ? fullScreenSource : sources[selectedSourceIndex], sourceInfo),
    mediaContext: {
      ...(() => {
        const base = fullScreenSource?.sourceContext ?? sources[selectedSourceIndex].sourceContext ?? {};
        const {
          pageNumber: _stalePageFromContext,
          ...rest
        } = base as Record<string, unknown>;
        return rest;
      })(),
      transcriptPosition: 'below',
      showTranscriptMarkers: true,
      showTranscript: true,
      htmlChunk: sources[selectedSourceIndex].content ? sources[selectedSourceIndex].content.match(/page \d+/i)?.[0] || '' : '',
      pageNumber: parseCitationPageFromSourceLike(fullScreenSource ? fullScreenSource : sources[selectedSourceIndex]),
      startTime: fullScreenSource ? fullScreenSource.sourceContext?.startTime || 0 : sources[selectedSourceIndex].sourceContext?.startTime || 0,
      previewUrl: fullScreenSource ? fullScreenSource.previewFilePath || fullScreenSource.derived_object_url : sources[selectedSourceIndex].previewFilePath || sources[selectedSourceIndex].derived_object_url
    }
  }) as MediaObject, [sources, selectedSourceIndex, sourceInfo, fullScreenSource]);
  return <Card data-testid="SourceCard" ref={sourceCardRef} classNames={{
    base: 'flex-grow relative bg-white/5 p-2 sm:px-4 sm:py-2 rounded-sm sm:rounded-2xl shadow-xl min-h-[200px] w-full overflow-hidden',
    header: 'flex flex-col gap-1 items-stretch m-0 p-0 border-b-[1px] border-white/70 border-solid pb-2 mb-2',
    body: 'relative p-0 m-0 min-h-[200px] sm:min-h-[calc(100vh-400px)] overflow-hidden'
  }} style={{
    height: 'auto',
    alignSelf: 'flex-start'
  }} data-sentry-element="Card" data-sentry-component="SourceCard" data-sentry-source-file="sourceCard.tsx">
      <CardHeader data-sentry-element="CardHeader" data-sentry-source-file="sourceCard.tsx">
        <div className='flex flex-row w-full items-center gap-2'>
          <div className='flex-1 min-w-0 flex flex-col gap-0.5'>
            <div data-testid="SourceLabel" className="flex flex-col gap-1 text-white/90 select-text cursor-default">
              <span className='font-semibold leading-tight text-[16px] break-all'>
                {extractPathAfterBulk(sources[selectedSourceIndex].sourceUrl || '', true).truncatedFilePath}
              </span>
            </div>
            {!sources[selectedSourceIndex].sourceUrl?.endsWith('.mbox') && !sources[selectedSourceIndex].sourceUrl?.endsWith('.mbx') && !!sources[selectedSourceIndex].previewFilePath && <div className="flex flex-row gap-1 text-white/90 select-text cursor-default">
                <span className='font-semibold leading-tight text-[16px] text-white/70'>Item: </span>
                <span className='font-semibold leading-tight text-[16px] break-all'>
                  {sources[selectedSourceIndex].previewFilePath ? `${sources[selectedSourceIndex].previewFilePath.split('/').pop()}` : ''}
                </span>
              </div>}
          </div>
          <div className='flex-shrink-0 flex flex-row items-center'>
            <button aria-label="Close source card" data-testid="close-source-button" onClick={closeSources} onTouchEnd={e => {
            e.preventDefault();
            closeSources();
          }} className="min-w-[44px] min-h-[44px] flex items-center justify-center cursor-pointer rounded-md hover:bg-white/10 text-white/70 hover:text-white transition-colors">
              <XIcon size={20} data-sentry-element="XIcon" data-sentry-source-file="sourceCard.tsx" />
            </button>
          </div>
        </div>
        <div className='box-border flex flex-row gap-2 items-center text-white/70 rounded-sm pt-1 border-t-[1px] border-white/70 border-solid'>
          <Tooltip content="Download source" placement="bottom" delay={0} offset={2} showArrow={true} classNames={{
          content: 'bg-tooltip-bg text-white px-3 py-2 rounded-md text-sm',
          base: 'before:bg-tooltip-bg'
        }} data-sentry-element="Tooltip" data-sentry-source-file="sourceCard.tsx">
            <button className="flex h-[20px] w-[20px] items-center justify-center overflow-hidden cursor-pointer disabled:cursor-not-allowed hover:text-white/100 hover:bg-tooltip-bg p-1 rounded-sm" onClick={event => {
            handleDownload(sources[selectedSourceIndex]);
            recordStat('DownloadSourceButtonClicked', event, statsContext, !!featureFlags?.useIntercom);
          }} onTouchEnd={() => handleDownload(sources[selectedSourceIndex])} disabled={isRestrictedFileType(sources[selectedSourceIndex])} aria-label="Download source">
              <DownloadSimpleIcon data-testid={`download-source-trigger-icon-${answerIndex}-${selectedSourceIndex}`} size={20} data-sentry-element="DownloadSimpleIcon" data-sentry-source-file="sourceCard.tsx" />
            </button>
          </Tooltip>
          <Tooltip content="Add source to scope" placement="bottom" delay={0} offset={2} showArrow={true} classNames={{
          content: 'bg-tooltip-bg text-white px-3 py-2 rounded-md text-sm',
          base: 'before:bg-tooltip-bg'
        }} data-sentry-element="Tooltip" data-sentry-source-file="sourceCard.tsx">
            <div className='h-[28px] w-[28px] overflow-hidden'>
              <button className={`h-[20px] w-[20px] overflow-hidden cursor-pointer disabled:cursor-not-allowed hover:text-white/100 p-1 rounded-sm
                  ${addedSource === selectedSourceIndex ? 'bg-brand-blue hover:bg-brand-blue' : 'hover:bg-tooltip-bg'}
                `} onClick={async event => {
              setIsExpanded?.(true);
              await new Promise(resolve => setTimeout(resolve, 750));
              handleAddSourceToScope(sources[selectedSourceIndex], selectedSourceIndex);
              recordStat('AddSourceToScopeButtonClicked', event, statsContext, !!featureFlags?.useIntercom);
            }} onTouchEnd={async () => {
              setIsExpanded?.(true);
              await new Promise(resolve => setTimeout(resolve, 750));
              handleAddSourceToScope(sources[selectedSourceIndex], selectedSourceIndex);
            }}>
                {addedSource === selectedSourceIndex ? <CheckIcon size={20} /> : <FunnelIcon data-testid={`add-source-to-scope-trigger-icon-${answerIndex}-${selectedSourceIndex}`} size={20} />}
              </button>
              {addedSource === selectedSourceIndex ? null : <PlusIcon size={10} className='ml-[-11px] fill-white' />}
            </div>
          </Tooltip>
          <Tooltip content="Copy source file path" placement="bottom" delay={0} offset={2} showArrow={true} classNames={{
          content: 'bg-tooltip-bg text-white px-3 py-2 rounded-md text-sm',
          base: 'before:bg-tooltip-bg'
        }} data-sentry-element="Tooltip" data-sentry-source-file="sourceCard.tsx">
            <button className={`h-[20px] w-[20px] overflow-hidden cursor-pointer disabled:cursor-not-allowed hover:text-white/100 p-1 rounded-sm
                ${copiedSource === selectedSourceIndex ? 'bg-brand-blue hover:bg-brand-blue' : 'hover:bg-tooltip-bg'}
                `} onClick={event => {
            handleCopySourceForTimeline(sources[selectedSourceIndex], selectedSourceIndex);
            recordStat('CopySourcePathButtonClicked', event, statsContext, !!featureFlags?.useIntercom);
          }} onTouchEnd={() => handleCopySourceForTimeline(sources[selectedSourceIndex], selectedSourceIndex)}>
              {copiedSource === selectedSourceIndex ? <CheckIcon size={20} /> : <FileCloudIcon data-testid={`copy-source-trigger-icon-${answerIndex}-${selectedSourceIndex}`} size={20} />}
            </button>
          </Tooltip>
          {handleCopyToMediaShare && UsableMediaTypes.includes(getMediaType({
          mediaType: 'unknown',
          mediaUrl: sources[selectedSourceIndex]?.sourceUrl || sources[selectedSourceIndex]?.sourceFilePath || ''
        } as MediaObject, sources[selectedSourceIndex]?.sourceInfo)) && <Tooltip content={copiedToMediaShareIndex === selectedSourceIndex ? "Added" : "Add to Favorites"} placement="bottom" delay={0} offset={2} showArrow={true} classNames={{
          content: 'bg-tooltip-bg text-white px-3 py-2 rounded-md text-sm',
          base: 'before:bg-tooltip-bg'
        }}>
              <button className={`h-[20px] w-[20px] overflow-hidden cursor-pointer disabled:cursor-not-allowed p-1 rounded-sm ${copiedToMediaShareIndex === selectedSourceIndex ? 'bg-brand-blue hover:bg-brand-blue text-white' : 'hover:text-white/100 hover:bg-tooltip-bg'}`} onClick={event => {
            handleCopyToMediaShare(sources[selectedSourceIndex], selectedSourceIndex, () => {
              setCopiedToMediaShareIndex(selectedSourceIndex);
              setTimeout(() => setCopiedToMediaShareIndex(null), 1500);
            });
            recordStat('CopyToMediaShareButtonClicked', event, statsContext, !!featureFlags?.useIntercom);
          }} onTouchEnd={() => {
            handleCopyToMediaShare(sources[selectedSourceIndex], selectedSourceIndex, () => {
              setCopiedToMediaShareIndex(selectedSourceIndex);
              setTimeout(() => setCopiedToMediaShareIndex(null), 1500);
            });
          }} aria-label={copiedToMediaShareIndex === selectedSourceIndex ? "Added" : "Add to Favorites"}>
                {copiedToMediaShareIndex === selectedSourceIndex ? <CheckIcon size={20} /> : <BookmarksSimpleIcon size={20} weight="duotone" />}
              </button>
            </Tooltip>}
          <Tooltip content="Create timeline event from source" placement="bottom" delay={0} offset={2} showArrow={true} classNames={{
          content: 'bg-tooltip-bg text-white px-3 py-2 rounded-md text-sm',
          base: 'before:bg-tooltip-bg'
        }} data-sentry-element="Tooltip" data-sentry-source-file="sourceCard.tsx">
            <button aria-label={createdEventFromSource ? "Event created from source" : "Create timeline event from source"} className="h-[20px] w-[20px] overflow-hidden cursor-pointer disabled:cursor-not-allowed hover:text-white/100 hover:bg-tooltip-bg p-1 rounded-sm" onClick={event => {
            recordStat('CreateEventFromSourceButtonClicked', event, statsContext, !!featureFlags?.useIntercom);
            handleCreateEventFromSource(sources[selectedSourceIndex], epochTimeSecs);
          }} onTouchEnd={() => handleCreateEventFromSource(sources[selectedSourceIndex], epochTimeSecs)}>
              {createdEventFromSource ? <CheckIcon size={20} /> : <ListPlusIcon data-testid={`event-from-source-trigger-icon-${answerIndex}-${selectedSourceIndex}`} size={20} />}
            </button>
          </Tooltip>
        </div>
      </CardHeader>
      <CardBody className='flex min-w-0 flex-col gap-2 overflow-x-hidden' data-sentry-element="CardBody" data-sentry-source-file="sourceCard.tsx">
        {!loadingSourceInfo && isMetaFile(sourceCardMediaItem) && <MetaSocialViewerButton src={sourceCardMediaItem.srcToUse || sourceCardMediaItem.mediaUrl || ''} mediaItem={sourceCardMediaItem} caseId={caseId} />}
        <div className={`flex flex-row items-top justify-start w-full`}>
          {sources[selectedSourceIndex].content && sources[selectedSourceIndex].content.trim() !== '' && !sources[selectedSourceIndex].location?.includes('/bkb/slow/transcribe/') && !sources[selectedSourceIndex].location?.endsWith('.transcript.txt') && <div className='cursor-pointer w-[20px] h-[20px] pl-1 pr-2' onClick={() => {
          setIsCitationExpanded?.(!isCitationExpanded);
        }}>
              {isCitationExpanded ? <CaretDoubleUpIcon size={20} /> : <CaretDoubleDownIcon size={20} />}
            </div>}
          {(sources[selectedSourceIndex].page && !isNaN(parseInt(sources[selectedSourceIndex].page)) || sources[selectedSourceIndex].content && sources[selectedSourceIndex].content.trim() !== '' && !sources[selectedSourceIndex].location?.includes('/bkb/slow/transcribe/') && !sources[selectedSourceIndex].location?.endsWith('.transcript.txt')) && <div className={`box-border flex flex-col gap-0 ${isCitationExpanded ? '' : 'h-[21px] max-h-[21px] overflow-hidden mb-1 opacity-0 transition-opacity duration-300 ease-in-out'}`}>
              {sources[selectedSourceIndex].page && !isNaN(parseInt(sources[selectedSourceIndex].page)) && <div className='text-white font-normal text-base mb-2'>
                  Page {sources[selectedSourceIndex].page}
                </div>}
              {sources[selectedSourceIndex].content && sources[selectedSourceIndex].content.trim() !== '' && !sources[selectedSourceIndex].location?.includes('/bkb/slow/transcribe/') && !sources[selectedSourceIndex].location?.endsWith('.transcript.txt') && <div className='flex flex-col gap-2'>
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
              p: ({
                node,
                children
              }) => <span className='mb-2 border-l-4 border-l-brand-blue/25 border-solid pl-2 leading-normal'>{children}</span>,
              h2: ({
                node,
                children
              }) => <span className='mb-2 border-l-4 border-l-brand-blue/25 border-solid pl-2 leading-normal'>{children}</span>,
              li: ({
                node,
                children
              }) => <li className='mb-2 border-l-4 border-l-brand-blue/25 border-solid pl-2 leading-normal'>{children}</li>,
              a: ({
                node,
                children
              }) => <a className='text-brand-blue-light italic'>{children}</a>
            }}>
                    {sources[selectedSourceIndex].content?.replace('```json ', '')}
                  </ReactMarkdown>
                </div>}
            </div>}
        </div>
        {loadingSourceInfo ? <div className='text-white font-normal text-base mb-2 flex flex-col justify-center items-center gap-4 h-full'>
            <Spinner size='lg' color='white' className="opacity-70" />
            <div className='text-white font-normal text-base mb-2'>Loading source info...</div>
          </div> : <MediaDisplay ref={mediaDisplayRef} key={`${sources[selectedSourceIndex].sourceFilePath}-${sources[selectedSourceIndex].sourceContext?.startTime || 0}-${sources[selectedSourceIndex].sourceFilePath?.endsWith('.mbox') || sources[selectedSourceIndex].sourceFilePath?.endsWith('.mbx') ? sources[selectedSourceIndex].previewFilePath || sources[selectedSourceIndex].derived_object_url || '' : ''}`} isPresentation={false} isSourceCard={true} mediaItem={sourceCardMediaItem} containerHeight={500} containerWidth={420} caseId={caseId} hideMetaSocialViewerButton={true} />}
        {!!attachments && attachments.length > 0 && <div className='flex flex-col gap-1'>
            <div className='text-white font-normal text-base pb-2 select-none'></div>
            <div className='flex flex-row items-center gap-2 text-white font-normal text-base px-2 py-1 select-none'>
              <PaperclipIcon weight="duotone" className='w-6 h-6' />
              {attachments.length} {attachments.length === 1 ? 'Attachment' : 'Attachments'}
            </div>
            {attachments.map((attachment, index) => <div key={index} className='box-border flex flex-col gap-2 pb-2 border-[1px] border-dashed border-white/5 rounded-md p-2 hover:bg-white/5'>
                <div className='flex flex-row items-center justify-between px-2 pt-1'>
                  <div className='flex flex-row items-center gap-2'>
                    <button className={`px-2 py-1 rounded-md select-none
                        ${openAttachmentIndex !== null && openAttachmentIndex === attachment.attachmentIndex ? 'bg-gray-600 hover:bg-gray-600 text-white' : 'cursor-pointer bg-gray-800 hover:bg-gray-800 text-white/70 hover:text-white'}`} key={attachment.attachmentIndex} onClick={() => {
                if (openAttachmentIndex !== null && openAttachmentIndex === attachment.attachmentIndex) {
                  setOpenAttachmentIndex(null);
                } else {
                  setOpenAttachmentIndex(attachment.attachmentIndex);
                }
              }}>
                      <div className='flex flex-row items-center justify-between gap-2'>
                        {openAttachmentIndex !== null && openAttachmentIndex === attachment.attachmentIndex ? <CaretDoubleUpIcon size={20} /> : <CaretDoubleDownIcon size={20} />}
                      </div>
                    </button>
                    <div className='text-white font-normal text-base'>{attachment.fileName}</div>
                  </div>
                  <div className='flex flex-row items-center gap-1'>
                    <Tooltip content="Copy attachment file path" placement="bottom" delay={0} offset={2} showArrow={true} classNames={{
                content: 'bg-tooltip-bg text-white px-3 py-2 rounded-md text-sm',
                base: 'before:bg-tooltip-bg'
              }}>
                      <button className={`h-[20px] w-[20px] overflow-hidden cursor-pointer disabled:cursor-not-allowed hover:text-white/100 p-1 rounded-sm
                          ${copiedAttachementIndex === attachment.attachmentIndex ? 'bg-brand-blue hover:bg-brand-blue' : 'hover:bg-tooltip-bg'}
                        `} onClick={event => {
                  handleCopyAttachmentSourceForTimeline(attachment, attachment.attachmentIndex);
                  recordStat('CopyAttachmentSourcePathButtonClicked', event, statsContext, !!featureFlags?.useIntercom);
                }} onTouchEnd={() => handleCopyAttachmentSourceForTimeline(attachment, attachment.attachmentIndex)}>
                        {copiedAttachementIndex === attachment.attachmentIndex ? <CheckIcon size={20} /> : <FileCloudIcon size={20} />}
                      </button>
                    </Tooltip>
                    {handleCopyToMediaShare && UsableMediaTypes.includes(getMediaType({
                mediaType: 'unknown',
                mediaUrl: attachment.attachmentUrl
              } as MediaObject, null)) && <Tooltip content={copiedToMediaShareAttachmentIndex === attachment.attachmentIndex ? "Added" : "Add to Favorites"} placement="bottom" delay={0} offset={2} showArrow={true} classNames={{
                content: 'bg-tooltip-bg text-white px-3 py-2 rounded-md text-sm',
                base: 'before:bg-tooltip-bg'
              }}>
                        <button className={`h-[20px] w-[20px] overflow-hidden cursor-pointer disabled:cursor-not-allowed p-1 rounded-sm ${copiedToMediaShareAttachmentIndex === attachment.attachmentIndex ? 'bg-brand-blue hover:bg-brand-blue text-white' : 'hover:text-white/100 hover:bg-tooltip-bg'}`} onClick={event => {
                  const sourceFromAttachment = {
                    sourceFilePath: attachment.attachmentUrl,
                    sourceUrl: attachment.attachmentUrl,
                    sourceContext: {
                      attachmentUrl: attachment.attachmentUrl
                    }
                  } as Source;
                  handleCopyToMediaShare(sourceFromAttachment, -1 - attachment.attachmentIndex, () => {
                    setCopiedToMediaShareAttachmentIndex(attachment.attachmentIndex);
                    setTimeout(() => setCopiedToMediaShareAttachmentIndex(null), 1500);
                  });
                  recordStat('CopyAttachmentToMediaShareButtonClicked', event, statsContext, !!featureFlags?.useIntercom);
                }} onTouchEnd={() => {
                  const sourceFromAttachment = {
                    sourceFilePath: attachment.attachmentUrl,
                    sourceUrl: attachment.attachmentUrl,
                    sourceContext: {
                      attachmentUrl: attachment.attachmentUrl
                    }
                  } as Source;
                  handleCopyToMediaShare(sourceFromAttachment, -1 - attachment.attachmentIndex, () => {
                    setCopiedToMediaShareAttachmentIndex(attachment.attachmentIndex);
                    setTimeout(() => setCopiedToMediaShareAttachmentIndex(null), 1500);
                  });
                }} aria-label={copiedToMediaShareAttachmentIndex === attachment.attachmentIndex ? "Added" : "Add to Favorites"}>
                          {copiedToMediaShareAttachmentIndex === attachment.attachmentIndex ? <CheckIcon size={20} /> : <BookmarksSimpleIcon size={20} weight="duotone" />}
                        </button>
                      </Tooltip>}
                    <Tooltip content="Create timeline event from attachment" placement="bottom" delay={0} offset={2} showArrow={true} classNames={{
                content: 'bg-tooltip-bg text-white px-3 py-2 rounded-md text-sm',
                base: 'before:bg-tooltip-bg'
              }}>
                      <button aria-label={createdEventFromAttachment === attachment.attachmentIndex ? "Event created from attachment" : "Create timeline event from attachment"} className="h-[20px] w-[20px] overflow-hidden cursor-pointer disabled:cursor-not-allowed hover:text-white/100 hover:bg-tooltip-bg p-1 rounded-sm" onClick={event => {
                  recordStat('CreateEventFromAttachmentButtonClicked', event, statsContext, !!featureFlags?.useIntercom);
                  handleCreateEventFromAttachment(sources[selectedSourceIndex], attachment, attachment.attachmentIndex);
                }} onTouchEnd={() => handleCreateEventFromAttachment(sources[selectedSourceIndex], attachment, attachment.attachmentIndex)}>
                        {createdEventFromAttachment === attachment.attachmentIndex ? <CheckIcon size={20} /> : <ListPlusIcon size={20} />}
                      </button>
                    </Tooltip>
                  </div>
                </div>
                {openAttachmentIndex !== null && openAttachmentIndex === attachment.attachmentIndex && <MediaDisplay key={`${attachment.attachmentUrl}`} isPresentation={false} isSourceCard={true} caseId={caseId} hideMetaSocialViewerButton={true} mediaItem={{
            mediaType: getMediaType({
              mediaUrl: attachment.attachmentUrl,
              mediaType: 'unknown'
            }, null),
            mediaUrl: attachment.attachmentUrl
          } as MediaObject} />}
              </div>)}
          </div>}
      </CardBody>
    </Card>;
};